import { analyzeUpload, saveBatch } from "./api";
import {
  classifyError,
  clearInput,
  dependenciesToCreate,
  hasValidExtension,
  isAnalysis,
  splitErrors
} from "./analysis";
import {
  AUTH_ERROR,
  GENERIC_ERROR,
  INVALID_EXTENSION,
  INVALID_QUERY,
  NETWORK_BATCH_SUFFIX,
  NETWORK_ERROR,
  REFRESH_FAILED,
  REFRESH_FAILED_WITH_DEPENDENCIES,
  SERVER_ERROR,
  STORAGE_ERROR,
  UNEXPECTED_SUCCESS,
  columnErrorsDialog,
  errorDialog,
  largeFileDialog,
  newDependenciesDialog,
  noValidRowsDialog,
  noticeDialog,
  rowErrorsConfirmDialog,
  successMessage,
  successWithErrorsDialog
} from "./messages";

const LARGE_FILE_ROWS = 1000;
const STOP = { proceed: false };

let busy = false;

function openLoading(vm) {
  vm.$store.commit("loading1", true);
}

function closeLoading(vm) {
  vm.$store.commit("loading1", false);
}

function storageError(err) {
  const error = err instanceof Error ? err : new Error("storage");
  error.stage = "storage";
  return error;
}

function uploadToStorage(vm, file, onProgress) {
  return new Promise(function(resolve, reject) {
    let task;
    try {
      task = vm.$fire.refexcel(file);
    } catch (err) {
      reject(storageError(err));
      return;
    }
    onProgress(10);
    task.on(
      "state_changed",
      function(snapshot) {
        const percentage =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (percentage > 10) onProgress(percentage);
      },
      function(err) {
        reject(storageError(err));
      },
      async function() {
        try {
          const url = await task.snapshot.ref.getDownloadURL();
          onProgress(0);
          resolve(url);
        } catch (err) {
          reject(storageError(err));
        }
      }
    );
  });
}

async function safeRefresh(refresh) {
  try {
    const result = await refresh();
    return result !== false;
  } catch (err) {
    return false;
  }
}

async function review(vm, analysis, fromBatch) {
  const split = splitErrors(analysis.errors);
  if (split.columnErrors.length > 0) {
    await vm.$swal(columnErrorsDialog(split.columnErrors));
    return STOP;
  }
  if (analysis.filas.validas === 0) {
    await vm.$swal(noValidRowsDialog(split.rowErrors));
    return STOP;
  }
  const large = analysis.filas.total > LARGE_FILE_ROWS;
  let confirmed = false;
  let noticeShown = false;
  if (split.rowErrors.length > 0) {
    const answer = await vm.$swal(
      rowErrorsConfirmDialog(split.rowErrors, analysis.filas, large)
    );
    if (!answer.isConfirmed) return STOP;
    confirmed = true;
    noticeShown = large;
  }
  if (analysis.dependenciasNuevas.length > 0) {
    const answer = await vm.$swal(
      newDependenciesDialog(analysis.dependenciasNuevas, large && !noticeShown)
    );
    if (!answer.isConfirmed) return STOP;
    confirmed = true;
    noticeShown = noticeShown || large;
  }
  if (!confirmed && fromBatch) {
    await vm.$swal(errorDialog(GENERIC_ERROR));
    return STOP;
  }
  if (!confirmed && large) {
    const answer = await vm.$swal(largeFileDialog());
    if (!answer.isConfirmed) return STOP;
  }
  return { proceed: true, dependenciasACrear: dependenciesToCreate(analysis) };
}

function isSuccessBody(result) {
  return (
    !!result &&
    typeof result === "object" &&
    typeof result.creados === "number" &&
    typeof result.actualizados === "number" &&
    Array.isArray(result.dependenciasCreadas) &&
    Array.isArray(result.errors)
  );
}

async function finishSuccess(vm, result, options) {
  if (!isSuccessBody(result)) {
    await safeRefresh(options.refresh);
    closeLoading(vm);
    await vm.$swal(noticeDialog(UNEXPECTED_SUCCESS, "info"));
    return;
  }
  const refreshed = await safeRefresh(options.refresh);
  closeLoading(vm);
  if (result.errors.length === 0) {
    vm.$store.dispatch("notify", {
      message: successMessage(result),
      type: "is-success",
      duration: 6000
    });
  } else {
    await vm.$swal(successWithErrorsDialog(result));
  }
  if (!refreshed) {
    const text =
      result.dependenciasCreadas.length > 0
        ? REFRESH_FAILED_WITH_DEPENDENCIES
        : REFRESH_FAILED;
    await vm.$swal(noticeDialog(text, "warning"));
  }
}

function errorText(err, stage) {
  if (err && err.stage === "storage") return { text: STORAGE_ERROR };
  if (err && err.invalidResponse) return { text: GENERIC_ERROR };
  const info = classifyError(err);
  const inBatch = stage === "batch";
  if (info.kind === "network") {
    return inBatch
      ? { text: `${NETWORK_ERROR} ${NETWORK_BATCH_SUFFIX}`, refresh: true }
      : { text: NETWORK_ERROR };
  }
  if (info.kind === "auth") return { text: AUTH_ERROR };
  if (info.kind === "server") {
    const gateway = info.status >= 502 && info.status <= 504;
    if (inBatch && gateway) {
      return { text: NETWORK_BATCH_SUFFIX, refresh: true };
    }
    return { text: SERVER_ERROR };
  }
  if (info.kind === "message") {
    if (info.message === "invalid query") {
      return inBatch
        ? { text: INVALID_QUERY, refreshIfConfigured: true }
        : { text: GENERIC_ERROR };
    }
    return { text: info.message, refreshIfConfigured: inBatch };
  }
  return { text: GENERIC_ERROR };
}

async function reportError(vm, err, stage, options) {
  const outcome = errorText(err, stage);
  if (
    outcome.refresh ||
    (outcome.refreshIfConfigured && options.refreshOnBatchMessage)
  ) {
    await safeRefresh(options.refresh);
  }
  await vm.$swal(
    outcome.refresh
      ? noticeDialog(outcome.text, "warning")
      : errorDialog(outcome.text),
  );
}

export async function runExcelUpload(vm, options) {
  const input = options.input;
  const onProgress = options.onProgress || function() {};
  if (busy) {
    clearInput(input);
    vm.$store.dispatch("notify", {
      message: "Ya hay una carga en curso. Espera a que termine.",
      type: "is-warning",
    });
    return;
  }
  if (!hasValidExtension(options.file)) {
    clearInput(input);
    await vm.$swal(errorDialog(INVALID_EXTENSION));
    return;
  }
  busy = true;
  let stage = "storage";
  try {
    if (options.loadingBeforeUpload) openLoading(vm);
    const url = await uploadToStorage(vm, options.file, onProgress);
    stage = "analysis";
    openLoading(vm);
    let analysis = await analyzeUpload({
      url,
      proceso: options.proceso,
      tipo: options.tipo
    });
    if (!isAnalysis(analysis)) {
      const invalid = new Error("invalid analysis");
      invalid.invalidResponse = true;
      throw invalid;
    }
    let fromBatch = false;
    for (;;) {
      const decision = await review(vm, analysis, fromBatch);
      if (!decision.proceed) return;
      openLoading(vm);
      stage = "batch";
      let result;
      try {
        result = await saveBatch(options.batchPath, {
          url,
          proceso: options.proceso,
          dependenciasACrear: decision.dependenciasACrear
        });
      } catch (err) {
        const info = classifyError(err);
        if (info.kind !== "analysis") throw err;
        analysis = info.analysis;
        fromBatch = true;
        continue;
      }
      await finishSuccess(vm, result, options);
      return;
    }
  } catch (err) {
    await reportError(vm, err, stage, options);
  } finally {
    busy = false;
    onProgress(0);
    clearInput(input);
    closeLoading(vm);
  }
}



// WEBPACK FOOTER //
// ./src/components/climan/upload/flow.js